import { z } from 'zod';
import { externalLinkNotificationActionSchema, notificationActionSchema, notificationContentSchema, notificationSeveritySchema, staticNotificationActionTargetSchema, } from '../notifications/notifications.js';
// Announcement-only extension of the shared notification content: an optional emoji to show in place of
// the severity icon (e.g. 🎉 for a "what's new" banner). Regular in-app notifications have no use for a
// per-item icon override, so this stays scoped to announcements rather than widening NotificationContent.
export const announcementContentSchema = notificationContentSchema.extend({
    icon: z.string().optional(),
});
export const partnerCockpitBannerSchema = z.object({
    uuid: z.uuid(),
    severity: notificationSeveritySchema,
    content: announcementContentSchema,
    action: notificationActionSchema.nullable(),
    dismissable: z.boolean(),
});
export const partnerCockpitBannersSchema = z.array(partnerCockpitBannerSchema);
export const announcementTypeSchema = z.enum(['Welcome', 'General', 'Validated']);
export const AnnouncementTypeValues = announcementTypeSchema.enum;
export const announcementScopeSchema = z.enum(['Partner', 'AllPartners']);
export const AnnouncementScopeValues = announcementScopeSchema.enum;
// Global renders in the app shell on every page (AppLayout); OfferRequestDetail renders only on the offer
// request detail page. There is no per-page placement model beyond this one split yet.
export const announcementPlacementSchema = z.enum(['Global', 'OfferRequestDetail']);
export const AnnouncementPlacementValues = announcementPlacementSchema.enum;
export const listBannersQuerySchema = z.object({
    placement: announcementPlacementSchema.default('Global'),
});
// Ops-tool can only offer targets that need no entity uuid — a broadcast has no single partner-specific
// entity (e.g. one offer request) to point every recipient at. externalLink is included since it needs no
// entity either — it carries its own destination URL.
export const broadcastActionTargetSchema = staticNotificationActionTargetSchema.exclude(['verification']);
export const BroadcastActionTargetValues = broadcastActionTargetSchema.enum;
export const staticNotificationActionSchema = z.discriminatedUnion('target', [
    z.object({ target: broadcastActionTargetSchema }),
    externalLinkNotificationActionSchema,
]);
// Ops-tool authors an announcement by referencing translation keys that already exist in the
// translation store (the same content model notifications use) rather than free text — there is no
// mechanism yet to author new, ad hoc, per-locale copy from this form. See CreateAnnouncementCommand.
export const createAnnouncementInputSchema = z
    .object({
    severity: notificationSeveritySchema,
    content: announcementContentSchema,
    dismissable: z.boolean(),
    // Absent/null means it never expires on its own.
    expiresAt: z.iso.datetime().nullable().optional(),
    action: staticNotificationActionSchema.optional(),
    placement: announcementPlacementSchema.default('Global'),
})
    .refine((input) => !input.expiresAt || new Date(input.expiresAt).getTime() > Date.now(), {
    message: 'expiresAt must be in the future',
    path: ['expiresAt'],
});
export const operationsToolAnnouncementSchema = z.object({
    uuid: z.uuid(),
    type: announcementTypeSchema,
    severity: notificationSeveritySchema,
    content: announcementContentSchema,
    action: notificationActionSchema.nullable(),
    scope: announcementScopeSchema,
    placement: announcementPlacementSchema,
    partnerUuid: z.uuid().nullable(),
    dismissable: z.boolean(),
    active: z.boolean(),
    expiresAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const operationsToolAnnouncementsSchema = z.array(operationsToolAnnouncementSchema);
